/**
 * money.ts — การคำนวณเงินทั้งหมดของระบบ
 *
 * หลักการ: คำนวณบน "สตางค์" ที่เป็นจำนวนเต็มเสมอ ไม่คำนวณบน float
 * ระบบเดิม (Go) ใช้ float64 ตรงๆ ไม่ปัดเศษ ทำให้ยอดรวมคลาดเคลื่อนได้
 * ที่ขอบของ API/DB เราแปลงกลับเป็นบาททศนิยม 2 ตำแหน่ง
 */
export const SATANG_PER_BAHT = 100;
/** อัตรา VAT มาตรฐานของไทย */
export const DEFAULT_VAT_RATE = 7;
/** ปัดครึ่งขึ้นแบบออกห่างจากศูนย์ (0.5 -> 1, -0.5 -> -1) ตามที่งานบัญชีไทยคาดหวัง */
export function roundHalfAwayFromZero(value) {
    return value < 0 ? -Math.round(-value) : Math.round(value);
}
/** แปลงบาท (อาจมีทศนิยม) เป็นสตางค์จำนวนเต็ม */
export function toSatang(baht) {
    if (!Number.isFinite(baht))
        return 0;
    // คูณผ่าน string-safe path เพื่อลดปัญหา 1.005 * 100 = 100.49999999999999
    return roundHalfAwayFromZero(parseFloat((baht * SATANG_PER_BAHT).toFixed(4)));
}
/** แปลงสตางค์จำนวนเต็มกลับเป็นบาท ทศนิยม 2 ตำแหน่ง */
export function fromSatang(satang) {
    return roundHalfAwayFromZero(satang) / SATANG_PER_BAHT;
}
/** ปัดจำนวนบาทให้เหลือทศนิยม 2 ตำแหน่ง (ผ่านสตางค์ เพื่อความแน่นอน) */
export function round2(baht) {
    return fromSatang(toSatang(baht));
}
/**
 * คิด VAT ไปข้างหน้า: รู้มูลค่าก่อนภาษี -> หา VAT และยอดรวม
 * ใช้ในใบเสนอราคา / ใบกำกับภาษี
 */
export function addVat(base, rate = DEFAULT_VAT_RATE) {
    const baseSatang = toSatang(base);
    const vatSatang = rate === 0 ? 0 : roundHalfAwayFromZero((baseSatang * rate) / 100);
    return {
        base: fromSatang(baseSatang),
        vat: fromSatang(vatSatang),
        total: fromSatang(baseSatang + vatSatang),
        rate,
    };
}
/**
 * ถอด VAT ย้อนกลับ: รู้ยอดที่จ่ายจริง (รวม VAT แล้ว) -> แยกมูลค่าก่อนภาษี + VAT
 * ใช้ในหน้าบันทึกค่าใช้จ่าย เมื่อผู้ใช้กรอกยอดจากใบเสร็จที่รวม VAT มาแล้ว
 *
 * บังคับให้ base + vat = total เสมอ โดยยก error ของการปัดเศษไปไว้ที่ VAT
 * เพื่อไม่ให้ยอดรวมในเอกสารเพี้ยนจากที่จ่ายจริง
 */
export function extractVat(total, rate = DEFAULT_VAT_RATE) {
    const totalSatang = toSatang(total);
    if (rate === 0) {
        return { base: fromSatang(totalSatang), vat: 0, total: fromSatang(totalSatang), rate };
    }
    const baseSatang = roundHalfAwayFromZero((totalSatang * 100) / (100 + rate));
    const vatSatang = totalSatang - baseSatang;
    return {
        base: fromSatang(baseSatang),
        vat: fromSatang(vatSatang),
        total: fromSatang(totalSatang),
        rate,
    };
}
/**
 * คิดยอดค่าใช้จ่ายจากสิ่งที่ผู้ใช้กรอก
 * @param inputAmount ยอดที่กรอก
 * @param inputIncludesVat true = ยอดที่กรอกรวม VAT แล้ว, false = ยังไม่รวม
 * @param rate อัตรา VAT (0 = ไม่มี VAT)
 */
export function computeExpenseAmounts(inputAmount, inputIncludesVat, rate = DEFAULT_VAT_RATE) {
    return inputIncludesVat ? extractVat(inputAmount, rate) : addVat(inputAmount, rate);
}
/** คำนวณยอดบรรทัดเดียว */
export function computeLineAmount(line) {
    const gross = roundHalfAwayFromZero(line.quantity * toSatang(line.unitPrice));
    const net = gross - toSatang(line.discount ?? 0);
    return fromSatang(Math.max(0, net));
}
/**
 * คำนวณยอดรวมของเอกสารทั้งใบ
 *
 * นี่คือ single source of truth — ทั้ง web, mobile และ API ต้องเรียกฟังก์ชันนี้
 * ระบบเดิมให้ frontend คำนวณแล้วส่งยอดมาเก็บตรงๆ ซึ่งเชื่อถือไม่ได้
 */
export function computeDocumentTotals(input) {
    const vatRate = input.vatRate ?? DEFAULT_VAT_RATE;
    const lines = input.items.map((item) => ({
        amount: computeLineAmount(item),
    }));
    const subtotalSatang = lines.reduce((sum, line) => sum + toSatang(line.amount), 0);
    const discountSatang = Math.max(0, toSatang(input.discountAmount ?? 0));
    const beforeVatSatang = Math.max(0, subtotalSatang - discountSatang);
    const vat = addVat(fromSatang(beforeVatSatang), vatRate);
    return {
        lines,
        subtotal: fromSatang(subtotalSatang),
        discountAmount: fromSatang(discountSatang),
        amountBeforeVat: vat.base,
        vatRate,
        vatAmount: vat.vat,
        totalAmount: vat.total,
    };
}
//# sourceMappingURL=money.js.map